/**
 * Product Builder — unified offer line configuration resolution.
 * Single source for bundle row readiness: Edit/Personalize, Continue, subtitle gate.
 */
(function (global) {
  'use strict';

  /** @param {object|null|undefined} slot */
  function slotHasPropertyKeys(slot) {
    return !!(slot && slot.properties && Object.keys(slot.properties).length > 0);
  }

  /**
   * True when a saved bundle slot is a complete override (not partial / poisoned).
   * @param {object|null|undefined} slot
   * @param {() => string[]} missingLabelsFn
   */
  function isCompleteSlotOverride(slot, missingLabelsFn) {
    if (!slotHasPropertyKeys(slot)) return false;
    return missingLabelsFn().length === 0;
  }

  /**
   * Main-flow selections satisfy variant + non-empty checks for a bundle line item.
   * @param {object} item
   * @param {object} mainProduct
   * @param {object} mainSelections
   * @param {(item: object, selections: object) => string|null|undefined} resolveVariantId
   */
  function isMainFlowSufficientForItem(item, mainProduct, mainSelections, resolveVariantId) {
    if (!mainSelections || typeof mainSelections !== 'object') return false;
    if (!Object.keys(mainSelections).length) return false;
    var host = item || mainProduct;
    if (host && !host.isSimple && typeof resolveVariantId === 'function') {
      if (!resolveVariantId(host, mainSelections)) return false;
    }
    return true;
  }

  /**
   * BUILDER_DATA stores variants/options at top level; bundle items embed them per row.
   * Single-offer and main-product paths must use this host for parity with bundle rows.
   * @param {object|null|undefined} mainProduct
   * @param {object|null|undefined} state
   * @param {object|null|undefined} builderData
   */
  function getMainProductHost(mainProduct, state, builderData) {
    if (!mainProduct) return null;
    var data = builderData || {};
    var s = state || {};
    return Object.assign({}, mainProduct, {
      variants:
        (s.main && s.main.variants) || data.variants || mainProduct.variants || [],
      options:
        (s.main && s.main.options) ||
        data.optionsWithValues ||
        data.options ||
        mainProduct.options ||
        [],
    });
  }

  /** Manual bundle-row save — auto-sync must never overwrite. */
  function isBundleRowUserEdited(slot) {
    return !!(slot && slot.userEdited);
  }

  function shouldSkipBundleSyncForSlot(slot) {
    return isBundleRowUserEdited(slot);
  }

  function hasVariantIfNeeded(item, slot, hasResolvedVariant) {
    if (!item || item.isSimple) return true;
    if (slot && slot.id) return true;
    return !!hasResolvedVariant;
  }

  /**
   * Required, non-media personalization fields missing from effective props.
   * Shared by auto-sync leniency, Edit/Personalize, and Continue.
   */
  function computeVisibleRequiredFieldGaps(effectiveProps, validationFields, adapters) {
    adapters = adapters || {};
    var gaps = [];
    var isRequired = adapters.isFieldRequired || function () {
      return true;
    };
    var omitMedia = adapters.omitMediaField || function () {
      return false;
    };
    var getValue = adapters.getPropValueForField || function (props, field) {
      if (!field || !field.label) return undefined;
      return props && props[field.label];
    };
    var isEmpty = adapters.isPropertyEmpty || function (val) {
      return val == null || val === '' || (typeof val === 'string' && val.trim() === '');
    };

    (validationFields || []).forEach(function (field) {
      if (!field) return;
      if (!isRequired(field)) return;
      if (omitMedia(field)) return;
      var v = getValue(effectiveProps || {}, field);
      if (isEmpty(v)) gaps.push(field.label || '');
    });
    return gaps.filter(Boolean);
  }

  function countFilledVisibleFields(effectiveProps, validationFields, adapters) {
    adapters = adapters || {};
    var filled = 0;
    var isRequired = adapters.isFieldRequired || function () {
      return true;
    };
    var omitMedia = adapters.omitMediaField || function () {
      return false;
    };
    var getValue = adapters.getPropValueForField || function (props, field) {
      if (!field || !field.label) return undefined;
      return props && props[field.label];
    };
    var isEmpty = adapters.isPropertyEmpty || function (val) {
      return val == null || val === '' || (typeof val === 'string' && val.trim() === '');
    };

    (validationFields || []).forEach(function (field) {
      if (!field) return;
      if (!isRequired(field)) return;
      if (omitMedia(field)) return;
      var v = getValue(effectiveProps || {}, field);
      if (!isEmpty(v)) filled++;
    });
    return filled;
  }

  /**
   * Gate checkout when required AI fields are not finalizeReady (PB-AI-5).
   * @param {object} result
   * @param {string[]} aiGaps
   */
  function applyAiFinalizeCheckoutGate(result, aiGaps) {
    if (!aiGaps || !aiGaps.length) return result;
    result.checkoutReady = false;
    result.autoSyncedReady = false;
    var seen = {};
    (result.missingLabels || []).forEach(function (label) {
      seen[label] = true;
    });
    aiGaps.forEach(function (label) {
      if (!seen[label]) {
        result.missingLabels.push(label);
        seen[label] = true;
      }
    });
    return result;
  }

  /**
   * Single readiness verdict for a bundle / offer line.
   * @param {object} ctx
   * @returns {{ displayConfigured: boolean, checkoutReady: boolean, showSubtitle: boolean, missingLabels: string[], autoSyncedReady: boolean }}
   */
  function resolveBundleRowReadiness(ctx) {
    ctx = ctx || {};
    var item = ctx.item;
    var slot = ctx.slot;
    var userEdited = !!ctx.userEdited;
    var needsConfiguration = ctx.needsConfiguration !== false;
    var effectiveProps = ctx.effectiveProps || (slot && slot.properties) || {};
    var validationFields = ctx.validationFields || (item && item.config) || [];
    var missingStrictFn = ctx.getMissingStrictLabels || function () {
      return [];
    };
    var anchorComplete = !!ctx.anchorComplete;
    var adapters = {
      isFieldRequired: ctx.isFieldRequired,
      omitMediaField: ctx.omitMediaField,
      getPropValueForField: ctx.getPropValueForField,
      isPropertyEmpty: ctx.isPropertyEmpty,
    };

    var result = {
      displayConfigured: false,
      checkoutReady: false,
      showSubtitle: false,
      missingLabels: [],
      autoSyncedReady: false,
    };

    if (!needsConfiguration) {
      result.displayConfigured = true;
      result.checkoutReady = true;
      return applyAiFinalizeCheckoutGate(
        result,
        typeof ctx.getAiFinalizeGaps === 'function' ? ctx.getAiFinalizeGaps() : []
      );
    }

    var hasSlotProps = slotHasPropertyKeys(slot);
    var variantOk = hasVariantIfNeeded(item, slot, ctx.hasResolvedVariant);
    var missingStrict = missingStrictFn();
    result.missingLabels = missingStrict.slice();

    if (userEdited && hasSlotProps) {
      var propsOk = !!(slot.properties && Object.keys(slot.properties).length > 0);
      result.displayConfigured = propsOk;
      result.checkoutReady = propsOk && variantOk && !!(slot && slot.id);
      result.showSubtitle = true;
      return applyAiFinalizeCheckoutGate(
        result,
        typeof ctx.getAiFinalizeGaps === 'function' ? ctx.getAiFinalizeGaps() : []
      );
    }

    if (anchorComplete) {
      result.displayConfigured = true;
      result.checkoutReady = variantOk;
      result.showSubtitle = true;
      return applyAiFinalizeCheckoutGate(
        result,
        typeof ctx.getAiFinalizeGaps === 'function' ? ctx.getAiFinalizeGaps() : []
      );
    }

    if (hasSlotProps && missingStrict.length === 0) {
      result.displayConfigured = true;
      result.checkoutReady = variantOk;
      result.showSubtitle = true;
      return applyAiFinalizeCheckoutGate(
        result,
        typeof ctx.getAiFinalizeGaps === 'function' ? ctx.getAiFinalizeGaps() : []
      );
    }

    if (hasSlotProps && !userEdited) {
      var visibleGaps = computeVisibleRequiredFieldGaps(effectiveProps, validationFields, adapters);
      var filledVisible = countFilledVisibleFields(effectiveProps, validationFields, adapters);
      if (visibleGaps.length === 0 && variantOk && filledVisible > 0) {
        result.autoSyncedReady = true;
        result.displayConfigured = true;
        result.checkoutReady = true;
        result.showSubtitle = true;
        return applyAiFinalizeCheckoutGate(
          result,
          typeof ctx.getAiFinalizeGaps === 'function' ? ctx.getAiFinalizeGaps() : []
        );
      }
    }

    if (typeof ctx.mainFlowConfigured === 'function' && ctx.mainFlowConfigured()) {
      result.displayConfigured = true;
      result.checkoutReady = variantOk;
      result.showSubtitle = true;
      return applyAiFinalizeCheckoutGate(
        result,
        typeof ctx.getAiFinalizeGaps === 'function' ? ctx.getAiFinalizeGaps() : []
      );
    }

    result.displayConfigured = missingStrict.length === 0;
    result.checkoutReady = missingStrict.length === 0 && variantOk;
    result.showSubtitle = hasSlotProps || result.displayConfigured;
    return applyAiFinalizeCheckoutGate(
      result,
      typeof ctx.getAiFinalizeGaps === 'function' ? ctx.getAiFinalizeGaps() : []
    );
  }

  /**
   * Offer Continue button — user-saved bundle rows are ready when variant + props exist.
   * @param {{ userEdited?: boolean, slot?: object|null, strictComplete?: () => boolean, configured?: () => boolean }} ctx
   */
  function isBundleRowCheckoutReady(ctx) {
    if (ctx && ctx.userEdited && ctx.slot && ctx.slot.id && ctx.slot.properties) {
      return Object.keys(ctx.slot.properties).length > 0;
    }
    if (ctx && typeof ctx.strictComplete === 'function' && ctx.strictComplete()) return true;
    if (ctx && typeof ctx.configured === 'function' && ctx.configured()) return true;
    return false;
  }

  /**
   * Whether a bundle / single offer line should show configured (Edit) vs Personalize.
   * @param {object} ctx
   */
  function isOfferLineConfigured(ctx) {
    if (typeof ctx.resolveReadiness === 'function') {
      return !!ctx.resolveReadiness().displayConfigured;
    }

    var item = ctx.item;
    var mainProduct = ctx.mainProduct;
    var state = ctx.state || {};
    var mainSelections = (state.main && state.main.selections) || {};

    if (typeof ctx.bundleRowNeedsConfiguration === 'function' && !ctx.bundleRowNeedsConfiguration(item)) {
      return true;
    }

    var slot = ctx.slot;
    var missingFn = typeof ctx.getMissingFieldLabels === 'function'
      ? ctx.getMissingFieldLabels
      : function () { return ['__missing_fn__']; };

    if (isCompleteSlotOverride(slot, missingFn)) {
      return true;
    }

    /* Main-flow projection applies only to the anchor row (first PDP product index), not duplicate same-SKU lines. */
    if (item && mainProduct && String(item.id) === String(mainProduct.id)) {
      var rowIdx = typeof ctx.idx === 'number' ? ctx.idx : -1;
      var anchorIdx = typeof ctx.firstMainIdx === 'number' ? ctx.firstMainIdx : -1;
      var isAnchorRow = anchorIdx < 0 || rowIdx < 0 || rowIdx === anchorIdx;
      if (isAnchorRow) {
        var resolveVariantId = ctx.resolveVariantId || function () { return null; };
        if (isMainFlowSufficientForItem(item, mainProduct, mainSelections, resolveVariantId)) {
          return true;
        }
      }
    }

    if (typeof ctx.isStrictBundleRowComplete === 'function') {
      return ctx.isStrictBundleRowComplete();
    }

    return missingFn().length === 0;
  }

  /**
   * Safe standalone main-flow check — never delegates to isOfferLineConfigured (recursion).
   * Patched onto productBuilder so stale cached Liquid cannot reintroduce the cycle.
   * @param {object} pb
   */
  function patchProductBuilderOfferLineGuards(pb) {
    if (!pb || pb._pbOfferLinePatchV2) return pb;
    pb.getMainProductHost = function (mainProduct, s) {
      return getMainProductHost(mainProduct, s, global.BUILDER_DATA);
    };
    pb.isBundleRowUserEdited = function (bundleId, idx, s) {
      var bid = bundleId != null && bundleId !== '' ? String(bundleId) : '';
      var state = s || global.builderState || {};
      var slot = bid && state.bundle && state.bundle.configurations && state.bundle.configurations[bid]
        ? state.bundle.configurations[bid][idx]
        : null;
      return isBundleRowUserEdited(slot);
    };
    pb.isStandaloneMainFlowComplete = function (mainProduct, s) {
      if (typeof this.bundleRowNeedsConfiguration === 'function' && !this.bundleRowNeedsConfiguration(mainProduct)) {
        return true;
      }
      var selections = (s && s.main && s.main.selections) || {};
      var host = getMainProductHost(mainProduct, s, global.BUILDER_DATA);
      var resolveVariantId = typeof this.resolveBundleItemVariantId === 'function'
        ? function (it, sel) { return this.resolveBundleItemVariantId(it, sel); }.bind(this)
        : function () { return null; };
      return isMainFlowSufficientForItem(host, mainProduct, selections, resolveVariantId);
    };
    pb._pbOfferLinePatchV2 = 1;
    pb._pbOfferLinePatchV1 = 1;
    return pb;
  }

  var api = {
    slotHasPropertyKeys: slotHasPropertyKeys,
    isCompleteSlotOverride: isCompleteSlotOverride,
    isMainFlowSufficientForItem: isMainFlowSufficientForItem,
    getMainProductHost: getMainProductHost,
    isBundleRowUserEdited: isBundleRowUserEdited,
    shouldSkipBundleSyncForSlot: shouldSkipBundleSyncForSlot,
    hasVariantIfNeeded: hasVariantIfNeeded,
    computeVisibleRequiredFieldGaps: computeVisibleRequiredFieldGaps,
    countFilledVisibleFields: countFilledVisibleFields,
    resolveBundleRowReadiness: resolveBundleRowReadiness,
    isBundleRowCheckoutReady: isBundleRowCheckoutReady,
    isOfferLineConfigured: isOfferLineConfigured,
    patchProductBuilderOfferLineGuards: patchProductBuilderOfferLineGuards,
  };

  global.PBOfferLineResolver = api;

  (function scheduleOfferLinePatch() {
    var doc = global.document;
    if (!doc) return;
    function apply() {
      patchProductBuilderOfferLineGuards(global.productBuilder);
    }
    if (doc.readyState === 'loading') {
      doc.addEventListener('DOMContentLoaded', apply, { once: true });
    } else {
      apply();
    }
  })();

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
})(typeof window !== 'undefined' ? window : globalThis);
